import React from 'react';
import { Helmet } from 'react-helmet-async';

interface Author {
  name: string;
  url?: string;
}

interface Organization {
  name: string;
  url?: string;
  logo?: string;
}

export interface NewsArticleData {
  headline: string;
  description: string;
  author: Author;
  publisher: Organization;
  datePublished: string;
  dateModified?: string;
  image?: string;
  articleSection?: string;
  wordCount?: number;
  url: string;
}

export interface CreativeWorkData {
  name: string;
  description: string;
  author: Author;
  publisher: Organization;
  dateCreated: string;
  dateModified?: string;
  image?: string;
  genre?: string;
  keywords?: string[];
  url: string;
}

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  siteName?: string;
  // New props for structured data
  newsArticle?: NewsArticleData;
  creativeWork?: CreativeWorkData;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'LeapWard - 基于热点新闻的深度思考分析平台',
  description = '通过热点新闻案例学习深度分析方法，AI专家级解读，提升批判性思维和洞察力。每日精选案例，让思维过程变得清晰可见。',
  keywords = 'LeapWard,深度分析,批判性思维,新闻分析,AI解读,思维模型,案例学习,洞察力提升',
  image = '/og-image.jpg',
  url = window.location.href,
  type = 'website',
  siteName = 'LeapWard',
  newsArticle,
  creativeWork,
}) => {
  return (
    <Helmet>
      {/* 基本meta标签 */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="LeapWard" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="zh-CN" />
      
      {/* Open Graph标签 */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="zh_CN" />
      
      {/* Twitter Card标签 */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* 结构化数据 */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "description": description,
          "url": url,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${url}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* NewsArticle 结构化数据 */}
      {newsArticle && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": newsArticle.headline,
            "image": newsArticle.image || image,
            "datePublished": newsArticle.datePublished,
            "dateModified": newsArticle.dateModified || newsArticle.datePublished,
            "author": {
              "@type": "Person",
              "name": newsArticle.author.name,
              "url": newsArticle.author.url
            },
            "publisher": {
              "@type": "Organization",
              "name": newsArticle.publisher.name,
              "url": newsArticle.publisher.url,
              "logo": newsArticle.publisher.logo && {
                "@type": "ImageObject",
                "url": newsArticle.publisher.logo
              }
            },
            "description": newsArticle.description,
            "articleSection": newsArticle.articleSection,
            "wordCount": newsArticle.wordCount,
            "url": newsArticle.url,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": newsArticle.url
            }
          })}
        </script>
      )}
      
      {/* CreativeWork 结构化数据 */}
      {creativeWork && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            "name": creativeWork.name,
            "description": creativeWork.description,
            "author": {
              "@type": "Person",
              "name": creativeWork.author.name,
              "url": creativeWork.author.url
            },
            "publisher": {
              "@type": "Organization",
              "name": creativeWork.publisher.name,
              "url": creativeWork.publisher.url,
              "logo": creativeWork.publisher.logo && {
                "@type": "ImageObject",
                "url": creativeWork.publisher.logo
              }
            },
            "dateCreated": creativeWork.dateCreated,
            "dateModified": creativeWork.dateModified || creativeWork.dateCreated,
            "image": creativeWork.image || image,
            "url": creativeWork.url,
            "genre": creativeWork.genre,
            "keywords": creativeWork.keywords?.join(', '),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": creativeWork.url
            }
          })}
        </script>
      )}
      
      {/* 额外的SEO标签 */}
      <link rel="canonical" href={url} />
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      
      {/* 预加载关键资源 */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    </Helmet>
  );
};

export default SEOHead;